import React, { FC, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Card, CardBody, CardHeader } from 'reactstrap';
import {
  AutoCalibrationRule,
  fillPersonPlaceholders,
  PERSON_PLACEHOLDERS,
  preComplileCalibrationRule,
} from 'utils/models/Calibration';
import { getPlaceholderFormatters } from 'utils/models/CalibrationRuleConditions';
import { useCampaignFlow } from 'views/Administration/CampaignFlowContext';

interface Props {
  rule: AutoCalibrationRule | null;
}

// Rendered inside the rule editor form as a CUSTOM_COMPONENT, so it also
// receives the generic ValidatedInput props, which it ignores.
const AutoCalibrationRulePreview: FC<Props> = ({ rule }) => {
  const intl = useIntl();
  const { formatMessage } = intl;

  const sampleName = formatMessage({
    id: 'app.views.administration.campaign_flow.auto_calibration_rules.preview.sample_name',
    defaultMessage: 'Riley Chen',
  });
  const sampleManagerName = formatMessage({
    id: 'app.views.administration.campaign_flow.auto_calibration_rules.preview.sample_manager_name',
    defaultMessage: 'Sam Park',
  });

  const contextRatingScale = useCampaignFlow()?.ratingScale;

  const compiled = useMemo(() => {
    if (!rule) {
      return null;
    }
    // an empty scale falls back to the compiler's default 1 to 5
    const precompiled = preComplileCalibrationRule(
      rule,
      contextRatingScale?.length ? contextRatingScale : undefined,
      getPlaceholderFormatters(intl)
    );
    return {
      text: fillPersonPlaceholders(precompiled, {
        person: sampleName,
        manager: sampleManagerName,
      }),
      unresolved: precompiled.compiledTemplateVariableNames.filter(
        (v) => !PERSON_PLACEHOLDERS.includes(v)
      ),
    };
  }, [rule, contextRatingScale, sampleName, sampleManagerName, intl]);

  return (
    <Card className="mb-0">
      <CardHeader className="d-flex align-items-center justify-content-between">
        <h4 className="card-header-title">
          <FormattedMessage
            id="app.views.administration.campaign_flow.auto_calibration_rules.preview.title"
            defaultMessage="Live preview"
          />
        </h4>
        {compiled && compiled.unresolved.length === 0 && (
          <span className="badge bg-success-soft">
            <FormattedMessage
              id="app.views.administration.campaign_flow.auto_calibration_rules.preview.template_valid"
              defaultMessage="Template valid"
            />
          </span>
        )}
        {compiled && compiled.unresolved.length > 0 && (
          <span className="badge bg-warning-soft">
            <FormattedMessage
              id="app.views.administration.campaign_flow.auto_calibration_rules.preview.unresolved"
              defaultMessage="Unresolved: {placeholders}"
              values={{
                placeholders: intl.formatList(
                  compiled.unresolved.map((p) => `{${p}}`),
                  { type: 'conjunction', style: 'long' }
                ),
              }}
            />
          </span>
        )}
      </CardHeader>
      <CardBody>
        {compiled ? (
          <p
            className="fw-bold mb-1"
            data-testid="auto-calibration-rule-preview"
          >
            {compiled.text}
          </p>
        ) : (
          <p className="text-muted mb-1">
            <FormattedMessage
              id="app.views.administration.campaign_flow.auto_calibration_rules.preview.incomplete"
              defaultMessage="Complete the fields above to preview the flag."
            />
          </p>
        )}
        <small className="text-muted">
          <FormattedMessage
            id="app.views.administration.campaign_flow.auto_calibration_rules.preview.caption"
            defaultMessage="Compiled from the flag message and the current condition, using this cycle's rating scale."
          />
        </small>
      </CardBody>
    </Card>
  );
};

export default React.memo(AutoCalibrationRulePreview);
